import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

/* -------------------- Reveal-on-scroll hook -------------------- */
export function useReveal({ threshold = 0.15, rootMargin = "0px 0px -10% 0px" } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);
  return [ref, visible];
}

/* -------------------- Imagery -------------------- */
export const HERO_IMG = "https://images.pexels.com/photos/13327298/pexels-photo-13327298.jpeg";
export const MEAL_IMG = "https://images.pexels.com/photos/10506640/pexels-photo-10506640.jpeg";
export const TRAIN_IMG = "https://images.unsplash.com/photo-1628935291759-bbaf33a66dc6";

/* -------------------- Primitives -------------------- */
export const Eyebrow = ({ children, accent = true }) => (
  <div className={`label-eyebrow mb-3 ${accent ? "text-[#6B9BFF]" : "text-white/50"}`}>
    {children}
  </div>
);

export const Stat = ({ value, label }) => (
  <div className="px-6 py-7 text-center">
    <div className="font-display text-4xl text-[#6B9BFF] leading-none">{value}</div>
    <div className="label-eyebrow text-white/50 mt-2 text-[0.65rem]">{label}</div>
  </div>
);

/* Bento card with optional photographic background + icon corner.
   `index` controls the scroll-reveal stagger (each card delayed by index * 80ms). */
export const BentoCard = ({
  className = "",
  bgImage,
  bgPosition = "center",
  Icon,
  iconClass = "text-[#6B9BFF] border-[#6B9BFF]/40",
  eyebrow,
  title,
  desc,
  bullets,
  trace = false,
  children,
  testId,
  index = 0,
}) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      data-testid={testId}
      style={{ transitionDelay: `${index * 80}ms` }}
      className={`reveal ${visible ? "is-visible" : ""} relative overflow-hidden border border-white/10 bg-[#121212] p-7 md:p-8 group transition-all hover:border-white/25 ${
        trace ? "tracing-border" : ""
      } ${className}`}
    >
      {bgImage && (
        <>
          <div
            className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity"
            style={{ backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: bgPosition }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#121212] via-[#121212]/85 to-[#121212]/40" />
        </>
      )}
      <div className="relative h-full flex flex-col">
        {Icon && (
          <div className={`w-11 h-11 flex items-center justify-center mb-5 border ${iconClass}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {title && <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2.5">{title}</h3>}
        {desc && <p className="text-sm text-white/60 leading-relaxed">{desc}</p>}
        {bullets && (
          <ul className="mt-4 space-y-1.5 text-sm text-white/70">
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-[#6B9BFF] shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        )}
        {children}
      </div>
    </div>
  );
};

/* Reusable scroll-reveal wrapper for non-bento elements (e.g. how-it-works steps). */
export const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* Word-by-word heading that triggers when scrolled into view.
   `tokens` = array of { w: string, className?: string, mr?: string, br?: true }
   `headingClass` is applied to the wrapping <h2>. */
export const WordHeading = ({ tokens, headingClass = "", stagger = 110, startDelay = 0 }) => {
  const [ref, visible] = useReveal();
  return (
    <h2
      ref={ref}
      className={`word-reveal ${visible ? "is-visible" : ""} ${headingClass}`}
    >
      {tokens.map((tok, i) =>
        tok.br ? (
          <br key={`br-${i}`} />
        ) : (
          <span
            key={`${tok.w}-${i}`}
            className={`word-reveal-token ${tok.className || ""}`}
            style={{
              animationDelay: `${startDelay + i * stagger}ms`,
              marginRight: tok.mr ?? "0.25em",
            }}
          >
            {tok.w}
          </span>
        )
      )}
    </h2>
  );
};
